import { usePageMetadata } from '../../hooks/usePageMetadata';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArtistHeader from './components/ArtistHeader';
import SupportCard from './components/SupportCard';
import ReleaseGrid from './components/ReleaseGrid';
import MembershipTiers from './components/MembershipTiers';
import TrackHighlights from './components/TrackHighlights';
import UpcomingEvents from './components/UpcomingEvents';
import AboutSection from './components/AboutSection';
import StickyCTA from './components/StickyCTA';
import { PreviewBar } from '../../components/media/PreviewBar';
import { ApiError, API_BASE_URL } from '../../lib/api';
import { trackPreviewPlay } from '../../lib/analyticsApi';
import { LikeButton } from '../../components/engagement/LikeButton';
import {
  fetchArtistDetail,
  type ArtistDetailResponse,
  type ArtistReleaseResponse,
  type ArtistTopTrackResponse,
  type ArtistFeaturedTrackResponse,
  type ArtistCollaboratorResponse
} from '../../lib/artistsApi';
import { Header } from '../../components/feature/Header';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { syncCartWithServer as syncCartWithServerApi } from '../../lib/cartSync';
import { useAuth } from '../../hooks/useAuth';
import { useLikes } from '../../context/LikesContext';
import { getDefaultReleaseCover, getImageVariant } from '../../lib/media';

interface Artist {
  id: string;
  name: string;
  slug: string;
  avatar: string;
  heroImageDesktop: string;
  heroImageMobile: string;
  city: string;
  lanes: string[];
  bioShort: string;
  bioLong: string;
  presskitUrl?: string | null;
  verified: boolean;
  socials: {
    instagram?: string;
    twitter?: string;
    youtube?: string;
    soundcloud?: string;
    tiktok?: string;
  };
  kpis: {
    gmv: number;
    units: number;
    topCity: string;
    followers: number;
  };
  hasNewRelease: boolean;
  followable: boolean;
}

interface Release {
  id: string;
  slug: string;
  title: string;
  cover: string;
  price: number;
  type: string;
  releaseDate: string;
  isLimited: boolean;
  copiesLeft?: number;
  totalCopies?: number;
  sales: number;
  tips: number;
  previewTrackId?: number | null;
  previewTrackTitle?: string | null;
  previewTrackDuration?: number | null;
  previewTrackLikes?: number | null;
}

interface MembershipTier {
  id: string;
  name: string;
  price: number;
  perks: string[];
  memberCount: number;
}

interface Track {
  id: string;
  title: string;
  duration: number;
  plays: number;
  previewUrl: string;
  releaseId: number;
  releaseSlug: string;
  releaseTitle: string;
  releaseCover?: string | null;
  likes: number;
  price?: number | null;
  primaryArtist?: string;
  isFeatured?: boolean;
}

interface Collaborator {
  id: string;
  name: string;
  slug: string;
  avatarUrl?: string | null;
  trackCount: number;
  releaseCount: number;
}

interface Event {
  id: string;
  title: string;
  date: string;
  venue: string;
  ticketUrl: string;
}

const PLACEHOLDER_AVATAR = '/images/254artistdefault.png';
const PLACEHOLDER_HERO_DESKTOP = '/images/artist_hero_savanna_desktop.png';
const PLACEHOLDER_HERO_MOBILE = '/images/artist_hero_savanna_mobile.png';
const PLACEHOLDER_COVER = getDefaultReleaseCover('medium');
const DEFAULT_TRACK_DURATION = 180;
const PREVIEW_SNIPPET_SECONDS = 30;

const parseLanes = (lanes: string | null): string[] => {
  if (!lanes) return [];
  try {
    const parsed = JSON.parse(lanes);
    if (Array.isArray(parsed)) {
      return parsed.map((lane) => String(lane)).filter(Boolean);
    }
  } catch {
    // Ignore malformed lane data
  }
  return [];
};

const formatReleaseTypeLabel = (type: string) => {
  switch (type) {
    case 'single':
      return 'Single';
    case 'ep':
      return 'EP';
    case 'album':
      return 'Album';
    case 'beat':
      return 'Beat';
    case 'bundle':
      return 'Bundle';
    default:
      return type;
  }
};

const formatTrackPriceLabel = (price?: number | null) => {
  if (price === null) {
    return 'Release only';
  }
  if (price === undefined) {
    return '—';
  }
  if (price === 0) {
    return 'Free';
  }
  return `KES ${price}`;
};

const mapReleaseFromResponse = (release: ArtistReleaseResponse): Release => {
  const limitedTotal = release.limited_total ?? undefined;
  const limitedSold = release.limited_sold ?? 0;
  const isLimited = typeof limitedTotal === 'number';
  const copiesLeft =
    isLimited && typeof limitedSold === 'number'
      ? Math.max(limitedTotal - limitedSold, 0)
      : undefined;

  const priceAmount =
    release.price_cents !== null ? release.price_cents : release.pwyw_min_cents ?? 0;

  return {
    id: String(release.id),
    slug: release.slug,
    title: release.title,
    cover: getImageVariant(release.cover_url, 'medium') || release.cover_url || PLACEHOLDER_COVER,
    price: priceAmount,
    type: formatReleaseTypeLabel(release.release_type),
    releaseDate: release.publish_at ?? new Date().toISOString(),
    isLimited,
    copiesLeft,
    totalCopies: limitedTotal,
    sales: limitedSold,
    tips: 0,
    previewTrackId: release.preview_track_id ?? null,
    previewTrackTitle: release.preview_track_title ?? null,
    previewTrackDuration: release.preview_track_duration ?? null,
    previewTrackLikes: release.preview_track_likes_count ?? 0,
  };
};

const mapArtistFromResponse = (
  detail: ArtistDetailResponse,
  hasNewRelease: boolean
): Artist => {
  const fallbackCity = detail.city || detail.country || 'Kenya';
  const heroImage = getImageVariant(detail.hero_image_url, 'large') || detail.hero_image_url || null;
  return {
    id: String(detail.id),
    name: detail.name,
    slug: detail.slug,
    avatar: getImageVariant(detail.avatar_url, 'medium') || detail.avatar_url || PLACEHOLDER_AVATAR,
    heroImageDesktop: heroImage ?? PLACEHOLDER_HERO_DESKTOP,
    heroImageMobile: heroImage ?? PLACEHOLDER_HERO_MOBILE,
    city: fallbackCity,
    lanes: parseLanes(detail.lanes),
    bioShort: detail.bio_short ?? 'Independent artist on 254Tracks.',
    bioLong: detail.bio_long ?? detail.bio_short ?? 'More info coming soon.',
    presskitUrl: detail.presskit_url,
    verified: Boolean(detail.is_verified),
    socials: {},
    kpis: {
      gmv: 0,
      units: 0,
      topCity: fallbackCity,
      followers: detail.likes_count ?? 0
    },
    hasNewRelease,
    followable: true
  };
};

const mapTopTrackFromResponse = (track: ArtistTopTrackResponse): Track => ({
  id: String(track.id),
  title: track.title,
  duration: track.duration_sec ?? DEFAULT_TRACK_DURATION,
  plays: track.preview_plays ?? 0,
  previewUrl: `${API_BASE_URL}/previews/${track.id}.mp3`,
  releaseId: track.release_id,
  releaseSlug: track.release_slug,
  releaseTitle: track.release_title,
  releaseCover: track.release_cover,
  likes: track.likes_count ?? 0,
});

const mapFeaturedTrackFromResponse = (
  track: ArtistFeaturedTrackResponse
): Track => ({
  id: String(track.id),
  title: track.title,
  releaseId: track.release_id,
  releaseSlug: track.release_slug,
  releaseTitle: track.release_title,
  releaseCover: track.release_cover || PLACEHOLDER_COVER,
  primaryArtist: track.primary_artist,
  price: track.price_cents,
  duration: track.duration_sec ?? DEFAULT_TRACK_DURATION,
  plays: 0,
  previewUrl: track.has_preview ? `${API_BASE_URL}/previews/${track.id}.mp3` : '',
  likes: track.likes_count ?? 0,
  isFeatured: true,
});

const mapCollaboratorFromResponse = (
  collaborator: ArtistCollaboratorResponse
): Collaborator => ({
  id: String(collaborator.id),
  name: collaborator.name,
  slug: collaborator.slug,
  avatarUrl: collaborator.avatar_url || null,
  trackCount: Number(collaborator.track_count) || 0,
  releaseCount: Number(collaborator.release_count) || 0,
});

export default function ArtistPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { items: cartItems, addItem } = useCart();
  const { addToast } = useToast();
  const { token } = useAuth();
  const [artist, setArtist] = useState<Artist | null>(null);
  usePageMetadata(artist ? `${artist.name} | 254Tracks` : 'Artist | 254Tracks', artist?.bioShort || 'Discover independent Kenyan artists on 254Tracks.', artist?.avatar);
  const [releases, setReleases] = useState<Release[]>([]);
  const [membershipTiers] = useState<MembershipTier[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [featuredTracks, setFeaturedTracks] = useState<Track[]>([]);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [events] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [releaseSort, setReleaseSort] = useState('latest');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { hydrateFromBootstrap: hydrateLikes } = useLikes();
  const [currentPreviewTrack, setCurrentPreviewTrack] = useState<Track | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [audioTime, setAudioTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(PREVIEW_SNIPPET_SECONDS);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const artistReleaseIds = useMemo(
    () =>
      releases
        .map((release) => Number(release.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [releases]
  );
  const releasePreviewTrackIds = useMemo(() => {
    const ids = releases
      .map((release) => release.previewTrackId)
      .filter((id): id is number => Number.isFinite(id));
    return new Set(ids);
  }, [releases]);
  const [isTipModalOpen, setIsTipModalOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState(250);
  const [isSavingTip, setIsSavingTip] = useState(false);
  const [tipError, setTipError] = useState<string | null>(null);
  const quickTipOptions = useMemo(() => [100, 250, 500], []);

  useEffect(() => {
    if (!slug) {
      setArtist(null);
      setReleases([]);
      setTracks([]);
      setFeaturedTracks([]);
      setCollaborators([]);
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    setLoadError(null);

    fetchArtistDetail(slug)
      .then((detail) => {
        if (cancelled) return;
        const hasLiveRelease = detail.releases.some(
          (release) => release.status === 'live'
        );
        setArtist(mapArtistFromResponse(detail, hasLiveRelease));
        setReleases(detail.releases.map(mapReleaseFromResponse));
        setTracks((detail.top_tracks ?? []).map(mapTopTrackFromResponse));
        setFeaturedTracks((detail.featured_tracks ?? []).map(mapFeaturedTrackFromResponse));
        setCollaborators((detail.collaborators ?? []).map(mapCollaboratorFromResponse));
        hydrateLikes?.(detail.likes?.artists ?? [], detail.likes?.tracks ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          setArtist(null);
          setReleases([]);
          setTracks([]);
          setFeaturedTracks([]);
          setCollaborators([]);
        } else {
          const message =
            err instanceof Error ? err.message : 'Failed to load artist.';
          setLoadError(message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'none';
    audioRef.current = audio;

    const handleTimeUpdate = () => setAudioTime(audio.currentTime);
    const handleLoaded = () => {
      if (Number.isFinite(audio.duration)) {
        setAudioDuration(audio.duration);
      }
    };
    const handleEnded = () => {
      setIsPreviewPlaying(false);
      setAudioTime(0);
    };
    const handleError = () => {
      setPreviewError('Preview unavailable. Try again later.');
      setIsPreviewPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoaded);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.pause();
      audio.src = '';
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoaded);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audioRef.current = null;
    };
  }, []);

  const stopPreview = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsPreviewPlaying(false);
    setAudioTime(0);
    setCurrentPreviewTrack(null);
  }, []);

  const togglePreviewPlayback = () => {
    const audio = audioRef.current;
    if (!audio || !currentPreviewTrack) {
      return;
    }
    if (isPreviewPlaying) {
      audio.pause();
      setIsPreviewPlaying(false);
    } else {
      audio
        .play()
        .then(() => {
          setIsPreviewPlaying(true);
        })
        .catch(() => {
          setPreviewError('Unable to play preview.');
        });
    }
  };

  const handlePreview = (track: Track) => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!track.previewUrl) {
      setPreviewError('Preview not available for this track.');
      return;
    }

    if (currentPreviewTrack?.id === track.id) {
      togglePreviewPlayback();
      return;
    }

    setPreviewError(null);
    setCurrentPreviewTrack(track);
    setAudioTime(0);
    setAudioDuration(PREVIEW_SNIPPET_SECONDS);

    if (audio.src !== track.previewUrl) {
      audio.src = track.previewUrl;
    }
    audio.currentTime = 0;
    audio
      .play()
      .then(() => {
        setIsPreviewPlaying(true);
        void trackPreviewPlay({
          trackId: Number(track.id),
          releaseId: track.releaseId,
          source: 'artist_page',
        });
      })
      .catch(() => {
        setPreviewError('Unable to play preview.');
        setIsPreviewPlaying(false);
      });
  };

  const handleReleasePreview = (release: Release) => {
    if (!release.previewTrackId) {
      setPreviewError('Preview not available for this release.');
      return;
    }
    handlePreview({
      id: String(release.previewTrackId),
      title: release.previewTrackTitle ?? release.title,
      duration: release.previewTrackDuration ?? DEFAULT_TRACK_DURATION,
      plays: 0,
      previewUrl: `${API_BASE_URL}/previews/${release.previewTrackId}.mp3`,
      releaseId: Number(release.id),
      releaseSlug: release.slug,
      releaseTitle: release.title,
      releaseCover: release.cover,
      likes: 0,
    });
  };

  useEffect(() => {
    if (!currentPreviewTrack) return;
    const existsInTop = tracks.some((track) => track.id === currentPreviewTrack.id);
    const existsInFeatured = featuredTracks.some(
      (track) => track.id === currentPreviewTrack.id
    );
    const existsInReleasePreviews = releasePreviewTrackIds.has(
      Number(currentPreviewTrack.id)
    );
    if (!existsInTop && !existsInFeatured && !existsInReleasePreviews) {
      stopPreview();
    }
  }, [tracks, featuredTracks, currentPreviewTrack, releasePreviewTrackIds, stopPreview]);

  const findTargetReleaseId = useCallback(() => {
    const idSet = new Set(artistReleaseIds);
    const match = cartItems.find((item) => idSet.has(Number(item.releaseId)));
    if (match) return match.releaseId ?? null;
    const firstRelease = artistReleaseIds[0];
    return firstRelease ?? null;
  }, [artistReleaseIds, cartItems]);

  const handleTip = () => {
    setTipError(null);
    setTipAmount((prev) => (prev > 0 ? prev : 250));
    setIsTipModalOpen(true);
  };

  const handleSubmitTip = async () => {
    if (!artist) return;
    if (!Number.isFinite(tipAmount) || tipAmount <= 0) {
      setTipError('Enter a tip amount above 0.');
      return;
    }
    const targetReleaseId = findTargetReleaseId();
    if (!targetReleaseId) {
      addToast({
        message: `Add a ${artist.name} release to your cart before tipping.`,
        variant: 'error',
      });
      return;
    }
    setIsSavingTip(true);
    setTipError(null);
    try {
      await syncCartWithServerApi({
        items: cartItems.filter((item) => item.kind !== 'tip'),
        tipCents: tipAmount,
        tipReleaseId: targetReleaseId,
        tipArtistId: Number(artist.id),
        tipArtistSlug: artist.slug,
        token,
      });
      addItem({
        id: `tip-${artist.slug}`,
        kind: 'tip',
        releaseId: targetReleaseId ?? 0,
        title: `Tip for ${artist.name}`,
        artist: artist.name,
        artistSlug: artist.slug,
        artistId: Number(artist.id),
        cover: artist.avatar,
        price: tipAmount,
        isPWYW: false,
        quantity: 1,
        formats: [],
        extras: [],
        tipSource: 'artist',
      });
      addToast({
        message: `Tip added for ${artist.name}`,
        variant: 'success',
        actionLabel: 'View cart',
        actionHref: '/cart',
      });
      setIsTipModalOpen(false);
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : 'Unable to add your tip right now. Please try again.';
      setTipError(message);
      addToast({ message, variant: 'error' });
    } finally {
      setIsSavingTip(false);
    }
  };

  const handleJoinMembership = (tierId: string) => {
    console.log('Join membership:', tierId);
  };

  const handleTrackLikeCountChange = useCallback((trackId: string, nextCount: number) => {
    setTracks((prev) =>
      prev.map((track) =>
        track.id === trackId
          ? {
              ...track,
              likes: nextCount,
            }
          : track,
      ),
    );
  }, []);

  const handleBuyRelease = (release: Release) => {
    if (!release?.slug) return;
    navigate(`/releases/${release.slug}`);
  };

  const primaryTierId = membershipTiers[0]?.id;
  const latestRelease = releases[0];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="bg-white rounded-lg p-8 mb-8">
              <div className="flex items-start gap-8">
                <div className="w-32 h-32 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-96"></div>
                </div>
                <div className="w-80 h-64 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg p-4">
                  <div className="aspect-square bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="flex items-center justify-center px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Artist Not Found</h1>
            <p className="text-gray-600 mb-8">The artist you're looking for doesn't exist.</p>
            <a
              href="/artists"
              className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Browse All Artists
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="flex items-center justify-center px-4 py-16">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Unable to load artist</h1>
            <p className="text-gray-600 mb-6">
              {loadError}
            </p>
            <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Try again
            </button>
            <a
              href="/artists"
              className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Browse Artists
            </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!artist) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      {/* Hero Background */}
      <div className="h-64 relative overflow-hidden">
        <picture className="absolute inset-0">
          <source media="(max-width: 640px)" srcSet={artist.heroImageMobile} />
          <img
            src={artist.heroImageDesktop}
            alt={`${artist.name} hero`}
            className="w-full h-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-yellow-500 to-red-500"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Artist Header */}
        <div className="relative -mt-32 mb-8">
          <div className="bg-white rounded-lg border border-neutral-200 p-8">
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <ArtistHeader artist={artist} />
              <SupportCard
                artist={artist}
                onTip={handleTip}
                membershipTiers={membershipTiers}
                onJoinMembership={handleJoinMembership}
                artistId={Number(artist.id)}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Releases */}
            <ReleaseGrid
              releases={releases}
              sortBy={releaseSort}
              onSortChange={setReleaseSort}
              onBuyRelease={handleBuyRelease}
              onPreviewRelease={handleReleasePreview}
              currentPreviewTrackId={currentPreviewTrack?.id ?? null}
              isPreviewPlaying={isPreviewPlaying}
            />
            {featuredTracks.length > 0 && (
              <section className="bg-white rounded-lg border border-neutral-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Featured On</h2>
                    <p className="text-sm text-gray-600">
                      Tracks from other releases that include {artist.name}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  {featuredTracks.map((track) => {
                    const previewAvailable = Boolean(track.previewUrl);
                    const isPlaying =
                      currentPreviewTrack?.id === track.id && isPreviewPlaying;
                    return (
                      <div
                        key={track.id}
                        className="flex flex-col sm:flex-row gap-4 border border-neutral-100 rounded-lg p-4 hover:border-emerald-200 transition-colors"
                      >
                        <div className="w-full sm:w-28 h-28 flex-shrink-0">
                          <img
                            src={track.releaseCover || PLACEHOLDER_COVER}
                            alt={track.releaseTitle}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs uppercase text-gray-500 mb-1">
                            {track.primaryArtist ?? track.releaseTitle}
                          </p>
                          <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                            {track.title}
                          </h3>
                          <p className="text-sm text-gray-600 line-clamp-1">
                            from {track.releaseTitle}
                          </p>
                          <div className="mt-2 flex items-center gap-3 text-sm text-gray-500">
                            <span className="inline-flex items-center gap-1">
                              <i className="ri-price-tag-3-line" />
                              {formatTrackPriceLabel(track.price)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center sm:flex-col gap-2 sm:gap-3">
                          <button
                            type="button"
                            onClick={() => handlePreview(track)}
                            disabled={!previewAvailable}
                            className={`px-4 py-2 rounded-lg text-sm font-medium w-full sm:w-auto ${
                              previewAvailable
                                ? isPlaying
                                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            <span className="flex items-center justify-center gap-2">
                              <i className={isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} />
                              {isPlaying ? 'Pause' : 'Preview'}
                            </span>
                          </button>
                          <LikeButton
                            subjectType="track"
                            subjectId={Number(track.id)}
                            initialCount={track.likes}
                            variant="badge"
                            size="sm"
                            className="sm:order-1"
                          />
                          <button
                            type="button"
                            onClick={() => navigate(`/releases/${track.releaseSlug}`)}
                            className="px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 w-full sm:w-auto"
                          >
                            View release
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Track Highlights */}
            <TrackHighlights
              tracks={tracks}
              currentPreview={
                currentPreviewTrack
                  ? {
                      track: currentPreviewTrack,
                      isPlaying: isPreviewPlaying,
                      currentTime: audioTime,
                    }
                  : null
              }
              onPreview={handlePreview}
              onLikeCountChange={handleTrackLikeCountChange}
            />

            {previewError && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                {previewError}
              </div>
            )}

            {/* About Section */}
            <AboutSection artist={artist} collaborators={collaborators} />
          </div>

          <div className="space-y-8">
            {/* Membership Tiers */}
            {membershipTiers.length > 0 && (
              <MembershipTiers
                tiers={membershipTiers}
                onJoinMembership={handleJoinMembership}
              />
            )}

            {/* Upcoming Events */}
            {events.length > 0 && (
              <UpcomingEvents events={events} />
            )}
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA */}
      <StickyCTA
        artist={artist}
        onTip={handleTip}
        onJoinMembership={() => {
          if (primaryTierId) {
            handleJoinMembership(primaryTierId);
          } else {
            handleTip();
          }
        }}
        onBuyLatest={() => {
          if (latestRelease) {
            handleBuyRelease(latestRelease);
          }
        }}
      />

      {/* Preview Bar */}
      {currentPreviewTrack && (
        <PreviewBar
          track={{
            cover: currentPreviewTrack.releaseCover ?? artist.heroImageDesktop ?? artist.avatar,
            title: currentPreviewTrack.title,
            releaseTitle: currentPreviewTrack.releaseTitle,
            artist: artist.name,
          }}
          isPlaying={isPreviewPlaying}
          currentTime={audioTime}
          duration={
            Number.isFinite(audioDuration) && audioDuration > 0
              ? audioDuration
              : PREVIEW_SNIPPET_SECONDS
          }
          onPlayPause={togglePreviewPlayback}
          onAddToCart={() => navigate(`/releases/${currentPreviewTrack.releaseSlug}`)}
          onClose={stopPreview}
          actionLabel="View release"
        />
      )}

      {isTipModalOpen && artist && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500">Support {artist.name}</p>
                <h3 className="text-lg font-semibold text-gray-900">Add a tip</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Tip goes straight to this artist. We&apos;ll attach it to their releases in your cart.
                </p>
              </div>
              <button
                onClick={() => {
                  setIsTipModalOpen(false);
                  setTipError(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close tip modal"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {quickTipOptions.map((amount) => (
                <button
                  key={amount}
                  onClick={() => {
                    setTipAmount(amount);
                    setTipError(null);
                  }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    tipAmount === amount
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-800 border-gray-200 hover:border-emerald-300 hover:text-emerald-700'
                  }`}
                >
                  KES {amount.toLocaleString()}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm text-gray-600">KES</span>
              <input
                type="number"
                min={1}
                value={Number.isFinite(tipAmount) ? tipAmount : ''}
                onChange={(event) => {
                  setTipAmount(Number(event.target.value));
                  setTipError(null);
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="250"
              />
            </div>
            {tipError && (
              <p className="text-sm text-red-600 mb-2">{tipError}</p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsTipModalOpen(false);
                  setTipError(null);
                }}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitTip}
                disabled={isSavingTip}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {isSavingTip ? 'Adding tip...' : 'Add tip to cart'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
